import React from "react";
import Skeleton from "react-loading-skeleton";
import { useCartDetailQuery, useUpdateCartItemsMutation } from "apis/campaignFlow";
import { fmtMoney } from "pages/CampaignFlow";
import AdminMarketingCart from "./AdminMarketingCart";

// Tier -> banner colour. Tier 1 (or unlocked) never renders a banner — the
// cart editor is enough; 2/3 are read-only in v1, no action buttons, just the
// reason why, straight from the backend.
const TIER_BANNER_COLORS = {
  2: "bg-yellow-50 text-yellow-800",
  3: "bg-red-50 text-red-800",
};

const CartCorrectionSection = ({ propertyId }) => {
  const { data, isPending } = useCartDetailQuery(propertyId);
  const updateMutation = useUpdateCartItemsMutation();

  if (isPending) return <Skeleton height={200} />;
  if (!data?.carts?.length) {
    return <p className="text-darkgray">No cart for this property yet.</p>;
  }

  // Every cart keyed by sequence, for AdminMarketingCart's cross-cart
  // free-inclusion dedupe — matches beleef-frontend's ScheduleCampaign.js
  // passing the same allCarts/cartKey shape into MarketingCart.
  const allCarts = Object.fromEntries(data.carts.map((c) => [String(c.sequence), c]));

  // Resolves with the SAVED cart (items/total as the server actually
  // persisted them — normalized product data, or free-inclusion lines the
  // unlocked path's saveCartsForProperty may have added/removed), not just
  // whatever the admin last had selected locally, so AdminMarketingCart can
  // re-sync to the canonical state after a successful save.
  const handleConfirm = (payload) =>
    new Promise((resolve, reject) => {
      updateMutation.mutate(
        { propertyId, ...payload },
        { onSuccess: (res) => resolve(res?.data?.data), onError: reject },
      );
    });

  return (
    <div>
      {data.carts.map((cart) => (
        <div key={cart.sequence} className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
            Cart {cart.sequence} — {fmtMoney(cart.total)}
          </div>
          {!cart.editable && cart.explanation ? (
            <div
              className={`rounded-lg p-3 text-sm ${TIER_BANNER_COLORS[cart.tier] || "bg-lightgray text-darkgray"}`}
            >
              {cart.explanation}
            </div>
          ) : (
            <AdminMarketingCart
              propertyId={propertyId}
              property={data.property}
              cart={cart}
              allCarts={allCarts}
              cartKey={String(cart.sequence)}
              onConfirm={handleConfirm}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default CartCorrectionSection;
